import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ToolCallData } from '../../api/types'
import { cn } from '../../lib/cn'
import { IconCheck, IconChevronRight, IconWarning } from '../icons'
import { Spinner } from '../ui/Spinner'
import { type FeedStep, diffStats, matchSubagents, runningStep, stepPreview, summarizeSteps, toolState, wordCount } from './feed'
import { SubagentRow } from './SubagentRow'
import type { SubagentLiveInfo } from './feed'

const MAX_OUTPUT = 6000

function Chevron({ open }: { open: boolean }) {
  return <IconChevronRight width={14} height={14} className={cn('shrink-0 text-ink-500 transition-transform', open && 'rotate-90')} />
}

function StatusIcon({ state }: { state: ReturnType<typeof toolState> }) {
  const { t } = useTranslation()
  if (state === 'running') return <Spinner className="h-3.5 w-3.5 shrink-0 border-[1.5px] text-ginger-500" />
  return state === 'error'
    ? <IconWarning width={14} height={14} className="shrink-0 text-red-600" aria-label={t('chat.tool.error')} />
    : <IconCheck width={14} height={14} className="shrink-0 text-mint-500" aria-label={t('chat.tool.done')} />
}

function DiffCounts({ added, removed }: { added: number; removed: number }) {
  return (
    <>
      {added > 0 && <span className="shrink-0 font-mono text-[11px] text-mint-500 tabular-nums">+{added}</span>}
      {removed > 0 && <span className="shrink-0 font-mono text-[11px] text-red-600 tabular-nums">−{removed}</span>}
    </>
  )
}

// Inside a group a step is a plain list row: the status icon carries status,
// so no caret and no shouting badge.
function ToolRow({ tool }: { tool: ToolCallData }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const state = toolState(tool)
  // The call is announced before its arguments exist: show what the model is
  // doing instead of an empty row.
  const preview = tool.streamingInput ? t('chat.tool.preparing') : stepPreview(tool.input)
  const output = tool.error ?? tool.output ?? (tool.permission === 'denied' ? 'denied' : '')
  // The preview drops a leading `cd <dir> &&`; the hover title keeps the full command.
  const command = typeof tool.input?.command === 'string' ? tool.input.command.trim() : ''
  const { added, removed } = diffStats([tool])
  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className="flex min-h-9 w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-cream-100"
      >
        <span className="shrink-0 rounded bg-cream-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-ink-300">{tool.tool}</span>
        <span
          title={command || preview}
          className={cn('min-w-0 flex-1 truncate font-mono text-ink-500', tool.streamingInput && 'animate-pulse')}
        >{preview}</span>
        <DiffCounts added={added} removed={removed} />
        <StatusIcon state={state} />
      </button>
      {open && (
        <div className="space-y-2 px-3 pb-3">
          <pre className="max-h-48 overflow-auto rounded-lg bg-cream-100 p-2.5 font-mono text-xs whitespace-pre-wrap text-ink-400">{JSON.stringify(tool.input, null, 2)}</pre>
          {output && (
            <pre className={cn('max-h-72 overflow-auto rounded-lg p-2.5 font-mono text-xs whitespace-pre-wrap',
              state === 'error' ? 'bg-red-50 text-red-700' : 'bg-cream-100 text-ink-300')}>
              {output.length > MAX_OUTPUT ? `${output.slice(0, MAX_OUTPUT)}\n…` : output}
            </pre>
          )}
        </div>
      )}
    </li>
  )
}

/** Collapsed "Thought · N words" row; opens to the reasoning text. */
export function ThoughtRow({ text, live, inline }: { text: string; live?: boolean; inline?: boolean }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const Tag = inline ? 'div' : 'li'
  return (
    <Tag>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className={cn('flex min-h-9 w-full items-center gap-2 py-1.5 text-left text-xs text-ink-500 hover:text-ink-300', inline ? 'px-0' : 'px-3 hover:bg-cream-100')}
      >
        <Chevron open={open} />
        <span className={cn(live && 'animate-pulse')}>{live ? t('chat.thinking') : t('chat.thought', { count: wordCount(text) })}</span>
      </button>
      {open && <p className={cn('pb-3 text-xs whitespace-pre-wrap text-ink-400 italic', inline ? 'pl-6' : 'px-3 pl-8')}>{text}</p>}
    </Tag>
  )
}

/**
 * One collapsed line per run of consecutive agent steps, as in meow-coding: what the run did ("Read 2
 * files, edited a.ts, ran a command"), its +N −M line counts and a failed count, without card chrome.
 * While a step runs, the line names it with a spinner and done/total progress. Opened, the steps sit
 * in one framed list.
 */
export function StepGroup({
  steps,
  liveThoughtId,
  subagents
}: {
  steps: FeedStep[]
  liveThoughtId?: string
  subagents?: Record<string, SubagentLiveInfo>
}) {
  const { t } = useTranslation()
  const tools = steps.flatMap(s => s.kind === 'tool' ? [s.tool] : [])
  const thoughts = steps.flatMap(s => s.kind === 'thought' ? [s] : [])
  // Each live subagent is joined to at most one card in this group, so a run's
  // tokens can never land on a card that already finished.
  const live = matchSubagents(tools, subagents)
  const states = tools.map(toolState)
  const running = tools.filter((_, i) => states[i] === 'running')
  const failed = states.filter(s => s === 'error').length
  const active = running[running.length - 1]
  const { added, removed } = diffStats(tools)
  const hasLiveThought = thoughts.some(s => s.id === liveThoughtId)
  // Collapsed by default. A running subagent is the exception: its card is
  // where that run's text and tools stream.
  const auto = running.some(tool => tool.tool === 'task')
  const [userOpen, setUserOpen] = useState<boolean | null>(null)
  const open = userOpen ?? auto

  let label: string
  if (active) {
    const { category, detail } = runningStep(active)
    label = `${t(detail ? `chat.stepRunning.${category}Named` : `chat.stepRunning.${category}`, { detail })}…`
  } else {
    const toolLabel = summarizeSteps(steps).map(({ category, count, name }, i) => {
      const part = name ? t(`chat.steps.${category}Named`, { name, count }) : t(`chat.steps.${category}`, { count })
      return i === 0 ? part : part.charAt(0).toLowerCase() + part.slice(1)
    }).join(', ')
    const totalWords = thoughts.reduce((sum, s) => sum + wordCount(s.text), 0)
    const thoughtLabel = thoughts.length > 0 ? t('chat.thought', { count: totalWords }) : ''
    label = [toolLabel, hasLiveThought ? t('chat.thinking') : thoughtLabel].filter(Boolean).join(', ')
  }

  return (
    <section className="max-w-full">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setUserOpen(!open)}
        className={cn('flex min-h-8 max-w-full items-center gap-2 py-1 text-left text-sm transition-colors hover:text-ink-200',
          active ? 'text-ink-200' : 'text-ink-400')}
      >
        {active && <Spinner className="h-3.5 w-3.5 shrink-0 border-[1.5px] text-ginger-500" />}
        <span className={cn('min-w-0 truncate', !active && hasLiveThought && 'animate-pulse')}>{label}</span>
        {active
          ? <span className="shrink-0 font-mono text-[11px] text-ink-500 tabular-nums">{tools.length - running.length}/{tools.length}</span>
          : <DiffCounts added={added} removed={removed} />}
        {failed > 0 && <span className="shrink-0 text-xs text-red-600">· {t('chat.steps.failed', { count: failed })}</span>}
        <Chevron open={open} />
      </button>
      {open && (
        <ul className="mt-1 divide-y divide-line overflow-hidden rounded-xl border border-line bg-cream-50">
          {steps.map(s => {
            if (s.kind === 'thought') {
              return <ThoughtRow key={s.id} text={s.text} live={s.id === liveThoughtId} />
            }
            if (s.tool.tool === 'task') {
              return <SubagentRow key={s.tool.id} tool={s.tool} subagent={live[s.tool.id]} />
            }
            return <ToolRow key={s.tool.id} tool={s.tool} />
          })}
        </ul>
      )}
    </section>
  )
}
